import { isEmpty } from 'ramda'

import { useCurrencySign, options as co } from 'app/common/components-next/currency'
import * as orgsTypes from 'app/common/constants/schools/types'
import * as rootRights from 'app/common/constants/rights/rootRights'
import * as specificRights from 'app/common/constants/rights/specificRights'
import { WithRightsForOrg } from 'app/rights/RightsForOrg'

import HeaderContent from './HeaderContent'
import LeftBarContent from './LeftBarContent'
import { MainKitContent } from './MainKitContent'
import RightBarContent from './RightBarContent'
import FooterContent from './FooterContent'
import useDeleteHideRestoreActions from './useDeleteHideRestoreActions'
import EmptyStub from './EmptyStub'
import { HeaderGroceryPrice } from './HeaderGroceryPrice/HeaderGroceryPrice'
import { ExtKitLayout } from './ExtKitLayout'
import { Complex } from '../kits2ExtendedItemsList'
import { ValidityByDate } from '../../../commonComponents/ValidityByDate'

import styles from './KitDishesExtended.scss'
import { DietFoodTypeId } from 'app/dataTypes/org/dietFoodTypes'
import { HeaderDietFoodType } from './HeaderDietFoodType/HeaderDietFoodType'

const complexesCompositionRigths = [
  rootRights.COMPLEXES,
  specificRights.COMPLEXES_COMPOSITION,
] as const

const buildPhotoUpdateKey = (params: Complex['params']): string => {
  const dishesKey = params.dishes
    .map(({ id, index, portion, cost }) => `${id}:${index}:${portion}:${cost}`)
    .join('|')

  return [
    params.id,
    params.fixedPriceEnabled ? 1 : 0,
    params.cost,
    params.grocerySetPrice ?? '',
    dishesKey,
  ].join(';')
}

type Props = {
  orgID: string
  orgType: string
  date: string
  kitId?: string
  useGrocerySetPrice: boolean
  action?: string
  loading: boolean
  basePath: string
  buildingId?: number
  dietFoodTypeId?: DietFoodTypeId
  dietFoodTypeTitle?: string
  schoolName?: string
  onRefresh?: () => void
} & Complex

export const KitDishesExtended = ({
  id,
  params,
  orgID,
  orgType,
  date,
  kitId = '',
  useGrocerySetPrice,
  action,
  subsidyOrders,
  deletable,
  loading,
  basePath,
  buildingId,
  dietFoodTypeId,
  dietFoodTypeTitle,
  schoolName,
  lastChange,
  onRefresh,
}: Props) => {
  const {
    title,
    cost,
    grocerySetPrice,
    dishes,
    fixedPriceEnabled,
    buffetZone,
    hotMealsZone,
    denyParentMoneySales,
    denyMoreThanOneSales,
    disableForTerminalGuestSales,
    useAsAdditional,
    fundMovementTypeCode,
  } = params

  const photoUpdateKey = buildPhotoUpdateKey(params)
  const currency = useCurrencySign(co.SPACE_BEFORE)
  const noDishes = isEmpty(dishes)
  const orgIdNum = parseInt(orgID, 10)
  const activeAction = parseInt(kitId, 10) === id && action ? action : null

  const [{ deleteKit, hideKit }, processing] = useDeleteHideRestoreActions(orgIdNum, id)
  const hideSomeOptions = orgType === orgsTypes.ENTERPRISE

  const content = {
    headerContent: (
      <HeaderContent
        id={id}
        orgID={orgID}
        buildingId={buildingId}
        dietFoodTypeId={dietFoodTypeId}
        date={date}
        title={title}
        cost={cost}
        currency={currency}
      />
    ),
    dietFoodType: !!dietFoodTypeTitle && <HeaderDietFoodType title={dietFoodTypeTitle} />,
    headerGroceryPrice: useGrocerySetPrice && (
      <HeaderGroceryPrice
        id={id}
        orgID={orgID}
        buildingId={buildingId}
        dietFoodTypeId={dietFoodTypeId}
        date={date}
        groceryPrice={grocerySetPrice}
        currency={currency}
        onRefresh={onRefresh}
      />
    ),
    leftBarContent: (
      <LeftBarContent
        id={id}
        orgID={orgID}
        buildingId={buildingId}
        dietFoodTypeId={dietFoodTypeId}
        title={title}
        date={date}
        subsidyOrders={subsidyOrders}
        updatePhotoKey={photoUpdateKey}
      />
    ),
    mainContent: noDishes ? (
      <EmptyStub />
    ) : (
      <div className={styles.mainContent}>
        <WithRightsForOrg pathTo={complexesCompositionRigths}>
          <ValidityByDate affect="hide">
            <MainKitContent
              className={styles.mainTable}
              dishes={dishes}
              currency={currency}
              orgID={orgID}
              id={id}
              buildingId={buildingId}
              dietFoodTypeId={dietFoodTypeId}
              date={date}
              title={title}
              schoolName={schoolName}
              onRefresh={onRefresh}
              // here disable prop is from WithRightsForOrg, ValidityByDate
            />
          </ValidityByDate>
        </WithRightsForOrg>
      </div>
    ),
    rightBarContent: (
      <RightBarContent
        id={id}
        title={title}
        orgID={orgID}
        buildingId={buildingId}
        dietFoodTypeId={dietFoodTypeId}
        date={date}
        noDishes={noDishes}
        subsidyOrders={subsidyOrders}
        deletable={deletable}
        fixedPriceEnabled={fixedPriceEnabled}
        activeAction={activeAction}
        deleteKit={deleteKit}
        hideKit={hideKit}
        hideDishes2Menu={hideSomeOptions}
        hideKit2Menu={hideSomeOptions}
        hideSettings={hideSomeOptions}
        buffetZone={buffetZone}
        hotMealsZone={hotMealsZone}
        denyParentMoneySales={denyParentMoneySales}
        denyMoreThanOneSales={denyMoreThanOneSales}
        disableForTerminalGuestSales={disableForTerminalGuestSales}
        useAsAdditional={useAsAdditional}
        fundMovementTypeCode={fundMovementTypeCode}
        lastChange={lastChange}
      />
    ),
    footerContent: (
      <FooterContent
        id={id}
        title={title}
        orgId={parseInt(orgID, 10)}
        date={date}
        loading={loading || processing}
        basePath={basePath}
        activeAction={activeAction}
        buildingId={buildingId}
        dietFoodTypeId={dietFoodTypeId}
      />
    ),
  }

  return <ExtKitLayout loading={loading || processing} {...content} />
}
